import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { BarChart3, CheckCircle, Shield, Target, Users, Lock, Briefcase, Percent, MapPin, GraduationCap, X, Star } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';
import axios from 'axios';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

export default function MonitoringEvaluation() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [experts, setExperts] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [filterSpec, setFilterSpec] = useState('');
  const [loadingExperts, setLoadingExperts] = useState(false);
  const [engaging, setEngaging] = useState(null);
  const [form, setForm] = useState({ project_brief: '', expected_duration: '', organization_name: '', contact_phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/experts/specializations`)
      .then(({ data }) => setSpecializations(data.specializations))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (authLoading || !user) return;
    fetchExperts();
  }, [user, authLoading, filterSpec]);

  const fetchExperts = async () => {
    setLoadingExperts(true);
    try {
      const params = filterSpec ? `?specialization=${encodeURIComponent(filterSpec)}` : '';
      const { data } = await axios.get(`${API_BASE_URL}/api/experts/${params}`, {
        headers: authHeaders(), withCredentials: true
      });
      setExperts(data.experts);
    } catch (e) {
      console.error('Error fetching experts:', e);
    } finally {
      setLoadingExperts(false);
    }
  };

  const submitEngagement = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/experts/${engaging.id}/engage`, form,
        { headers: authHeaders(), withCredentials: true }
      );
      setSuccessMsg(data.message);
      setEngaging(null);
      setForm({ project_brief: '', expected_duration: '', organization_name: '', contact_phone: '' });
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const initials = (name) => (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5F8D] text-white pt-32 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Monitoring & Evaluation
            </h1>
            <p className="text-lg sm:text-xl text-blue-100 leading-relaxed">
              Independent, expert-led M&E — delivered by CSED's empanelled panel of specialist consultants
            </p>
          </div>
        </div>
      </div>

      <main className="flex-grow">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-5xl mx-auto">
            {/* Introduction (condensed) */}
            <div className="mb-12">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                CSED's Monitoring & Evaluation (M&E) function is central to its role as a <strong>high-trust institutional platform</strong>.
                Across the development ecosystem, significant volumes of capital are deployed through CSR initiatives, public programs and
                philanthropic efforts — yet effectiveness is often constrained by gaps in measurement, governance and accountability.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                CSED bridges this gap through a <strong>consultant-led delivery model</strong>: independent domain specialists — inducted
                as Professional members of CSED — deliver structured M&E and allied advisory services, with CSED providing the
                institutional framework, quality assurance and coordination.
              </p>
            </div>

            {/* Consultant-Led Model */}
            <Card className="p-8 mb-12 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200" data-testid="consultant-model-card">
              <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4 flex items-center gap-3">
                <Users className="h-8 w-8 text-[#E87722]" />
                The Consultant-Led Delivery Model
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                CSED empanels vetted, independent professionals — <strong>M&E Consultants, Technical Consultants, Lawyers,
                Auditors</strong> and other domain experts — and showcases their profiles to its member network. CSR divisions,
                funding organisations and institutional members engage these experts directly through the platform.
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-sm text-center">
                  <div className="w-12 h-12 bg-[#E87722] text-white rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-xl font-bold">1</span>
                  </div>
                  <h3 className="font-bold text-[#1E3A5F] mb-2">Experts Inducted</h3>
                  <p className="text-sm text-gray-600">Specialist consultants join CSED as Professional members</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm text-center">
                  <div className="w-12 h-12 bg-[#E87722] text-white rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-xl font-bold">2</span>
                  </div>
                  <h3 className="font-bold text-[#1E3A5F] mb-2">Members Engage</h3>
                  <p className="text-sm text-gray-600">Institutional & CSR members browse profiles and engage experts via CSED</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm text-center">
                  <div className="w-12 h-12 bg-[#E87722] text-white rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-xl font-bold">3</span>
                  </div>
                  <h3 className="font-bold text-[#1E3A5F] mb-2">Assured Delivery</h3>
                  <p className="text-sm text-gray-600">Baseline → continuous monitoring → mid-term & end-line evaluation</p>
                </div>
              </div>
            </Card>

            {/* Core Principles (condensed) */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-[#1E3A5F] mb-8">Core Principles</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <Card className="p-6 hover:shadow-xl transition-shadow">
                  <Shield className="h-10 w-10 text-[#E87722] mb-3" />
                  <h3 className="text-lg font-bold text-[#1E3A5F] mb-2">Data Integrity</h3>
                  <p className="text-gray-700 text-sm">Accuracy and reliability across all measurement systems</p>
                </Card>
                <Card className="p-6 hover:shadow-xl transition-shadow">
                  <BarChart3 className="h-10 w-10 text-[#E87722] mb-3" />
                  <h3 className="text-lg font-bold text-[#1E3A5F] mb-2">Transparency</h3>
                  <p className="text-gray-700 text-sm">Clear, accessible reporting for all stakeholders</p>
                </Card>
                <Card className="p-6 hover:shadow-xl transition-shadow">
                  <Target className="h-10 w-10 text-[#E87722] mb-3" />
                  <h3 className="text-lg font-bold text-[#1E3A5F] mb-2">Impact Beyond Outputs</h3>
                  <p className="text-gray-700 text-sm">Evaluations assess outcomes and long-term impact — not just activity</p>
                </Card>
              </div>
            </div>

            {/* Expert Panel */}
            <div className="mb-12" id="expert-panel">
              <h2 className="text-3xl font-bold text-[#1E3A5F] mb-2">Our Expert Panel</h2>
              <p className="text-gray-600 mb-8">Vetted independent professionals available to CSED members</p>

              {successMsg && (
                <div className="mb-6 p-4 bg-green-50 border border-green-300 rounded-lg flex items-start justify-between gap-3" data-testid="engagement-success-msg">
                  <p className="text-green-800 text-sm"><CheckCircle className="h-4 w-4 inline mr-2" />{successMsg}</p>
                  <button onClick={() => setSuccessMsg('')}><X className="h-4 w-4 text-green-700" /></button>
                </div>
              )}

              {!authLoading && !user ? (
                <Card className="p-10 text-center border-2 border-dashed" data-testid="experts-locked-card">
                  <div className="mx-auto h-14 w-14 rounded-full bg-[#1E3A5F]/10 flex items-center justify-center mb-4">
                    <Lock className="h-7 w-7 text-[#1E3A5F]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1E3A5F] mb-2">Members Only</h3>
                  <p className="text-gray-600 mb-6 max-w-xl mx-auto">
                    Expert profiles — including photos, specialisations and experience — are visible to CSED members.
                    Log in or become a member to browse and engage the panel.
                  </p>
                  <div className="flex gap-3 justify-center flex-wrap">
                    <Button onClick={() => navigate('/login')} variant="outline" data-testid="experts-login-btn">Member Login</Button>
                    <Button onClick={() => navigate('/membership')} className="bg-[#E87722] hover:bg-[#d66a1a]" data-testid="experts-join-btn">
                      Join CSED
                    </Button>
                  </div>
                </Card>
              ) : (
                <>
                  {/* Specialization Filter */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    <button
                      onClick={() => setFilterSpec('')}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${!filterSpec ? 'bg-[#1E3A5F] text-white' : 'bg-white text-gray-600 border hover:border-[#1E3A5F]'}`}
                      data-testid="expert-filter-all"
                    >
                      All
                    </button>
                    {specializations.map((s) => (
                      <button
                        key={s}
                        onClick={() => setFilterSpec(s)}
                        className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${filterSpec === s ? 'bg-[#1E3A5F] text-white' : 'bg-white text-gray-600 border hover:border-[#1E3A5F]'}`}
                        data-testid={`expert-filter-${s.replace(/[^a-zA-Z]/g, '-')}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>

                  {loadingExperts ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#E87722] mx-auto"></div>
                    </div>
                  ) : experts.length === 0 ? (
                    <Card className="p-10 text-center" data-testid="experts-empty-state">
                      <Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-600">
                        {filterSpec ? `No ${filterSpec}s on the panel yet.` : 'Expert profiles are being onboarded. Please check back soon.'}
                      </p>
                    </Card>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-6">
                      {experts.map((ex) => (
                        <Card key={ex.id} className="p-6 hover:shadow-xl transition-all" data-testid={`expert-card-${ex.id}`}>
                          <div className="flex items-start gap-4">
                            {ex.photo_url ? (
                              <img src={ex.photo_url} alt={ex.name} className="h-20 w-20 rounded-full object-cover border-2 border-[#E87722]/30 flex-shrink-0" />
                            ) : (
                              <div className="h-20 w-20 rounded-full bg-[#1E3A5F] text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                                {initials(ex.name)}
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <h3 className="font-bold text-lg text-[#1E3A5F]">{ex.name}</h3>
                              <div className="flex items-center gap-2 flex-wrap mt-1">
                                <Badge className="bg-[#E87722]/10 text-[#E87722] border border-[#E87722]/30">{ex.specialization}</Badge>
                                {ex.avg_rating && (
                                  <span className="flex items-center gap-1 text-sm font-semibold text-yellow-600" data-testid={`expert-rating-${ex.id}`}>
                                    <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                                    {ex.avg_rating} <span className="text-xs text-gray-400 font-normal">({ex.rating_count})</span>
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                                {ex.years_experience ? <span className="flex items-center gap-1"><Briefcase className="h-3 w-3" />{ex.years_experience}+ yrs experience</span> : null}
                                {ex.location ? <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{ex.location}</span> : null}
                              </div>
                              {ex.qualifications && (
                                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1"><GraduationCap className="h-3 w-3 flex-shrink-0" />{ex.qualifications}</p>
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-gray-700 mt-4 line-clamp-3">{ex.bio}</p>
                          {ex.sectors && (
                            <p className="text-xs text-gray-500 mt-2"><strong>Sectors:</strong> {ex.sectors}</p>
                          )}
                          <Button
                            onClick={() => { setEngaging(ex); setSuccessMsg(''); }}
                            className="mt-4 bg-[#1E3A5F] hover:bg-[#2d5a8f] w-full"
                            data-testid={`engage-expert-btn-${ex.id}`}
                          >
                            Engage this Expert
                          </Button>
                        </Card>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Engagement Terms */}
            <Card className="p-8 mb-12 bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200" data-testid="engagement-terms-card">
              <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4 flex items-center gap-3">
                <Percent className="h-7 w-7 text-[#E87722]" />
                How Engagements Work
              </h2>
              <ul className="space-y-3">
                {[
                  'Members submit an engagement request through the platform; CSED coordinates introductions and scoping.',
                  'The engagement fee / professional fee is agreed directly between the member and the expert.',
                  '5% of the engagement fee / professional fee paid and collected is payable to CSED at the end of the assignment.',
                  'CSED provides institutional oversight and quality assurance throughout the engagement.'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#E87722] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <Button
                variant="outline"
                className="mt-6 border-[#E87722] text-[#E87722] hover:bg-[#E87722] hover:text-white"
                onClick={() => navigate('/payment')}
                data-testid="me-make-payment-btn"
              >
                Make a Payment to CSED
              </Button>
            </Card>

            {/* Closing Statement */}
            <div className="text-center bg-gradient-to-r from-[#1E3A5F] to-[#2D5F8D] text-white p-12 rounded-xl">
              <h2 className="text-2xl font-bold mb-4">Ensuring Effective Capital Deployment</h2>
              <p className="text-lg text-blue-100 leading-relaxed max-w-3xl mx-auto">
                Through expert-led M&E, CSED enables <strong>transparent, accountable, and measurable</strong> development
                outcomes across economic, social, and environmental domains.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Engagement Modal */}
      {engaging && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="engagement-modal">
          <Card className="w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-[#1E3A5F]">Engage {engaging.name}</h3>
                <p className="text-sm text-gray-500">{engaging.specialization}</p>
              </div>
              <button onClick={() => setEngaging(null)} data-testid="engagement-modal-close"><X className="h-5 w-5 text-gray-400" /></button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project Brief *</label>
                <Textarea
                  rows={4}
                  placeholder="Describe your program, what you need evaluated/advised on, geography, timelines..."
                  value={form.project_brief}
                  onChange={(e) => setForm({ ...form, project_brief: e.target.value })}
                  data-testid="engagement-brief-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expected Duration</label>
                <Input placeholder="e.g. 3 months, one-time assessment" value={form.expected_duration}
                  onChange={(e) => setForm({ ...form, expected_duration: e.target.value })}
                  data-testid="engagement-duration-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Organisation</label>
                <Input placeholder="Organisation name" value={form.organization_name}
                  onChange={(e) => setForm({ ...form, organization_name: e.target.value })}
                  data-testid="engagement-org-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
                <Input placeholder="+91..." value={form.contact_phone}
                  onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                  data-testid="engagement-phone-input" />
              </div>
              <p className="text-xs text-gray-500 bg-orange-50 border border-orange-200 rounded-lg p-3">
                Note: 5% of the engagement fee / professional fee paid and collected is payable to CSED at the end of the assignment.
              </p>
              <Button
                onClick={submitEngagement}
                disabled={submitting || form.project_brief.trim().length < 20}
                className="w-full bg-[#E87722] hover:bg-[#d66a1a]"
                data-testid="engagement-submit-btn"
              >
                {submitting ? 'Sending...' : 'Send Engagement Request to CSED'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      <Footer />
    </div>
  );
}
